import { useMutation } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/lib/api/auth'
import { graphqlErrorMessage } from '@/lib/api/client'
import { loginWithPersonalAccessToken } from '@/lib/api/pat-login'

export function PatLoginDialog({ onClose, onSuccess }: { onClose: () => void; onSuccess?: () => void }) {
  const { mode, signIn } = useAuth()
  const [token, setToken] = useState('')
  const login = useMutation({
    mutationFn: () => loginWithPersonalAccessToken(mode, token),
    onSuccess: (session) => {
      setToken('')
      onClose()
      signIn(session)
      onSuccess?.()
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !login.isPending) onClose()
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={(event) => {
          if (login.isPending) event.preventDefault()
        }}
        onInteractOutside={(event) => {
          if (login.isPending) event.preventDefault()
        }}
      >
        <DialogTitle>Logga in med åtkomsttoken</DialogTitle>
        <DialogDescription>Använd en personlig åtkomsttoken med behörighet till restaurangens köksskärm.</DialogDescription>
        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault()
            event.stopPropagation()
            if (!login.isPending) login.mutate()
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="pat-token">Åtkomsttoken</Label>
            <Input
              id="pat-token"
              type="password"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              value={token}
              onChange={(event) => {
                setToken(event.target.value)
                login.reset()
              }}
              disabled={login.isPending}
              required
            />
          </div>
          {login.isError ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {graphqlErrorMessage(login.error)}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={login.isPending}>
              Avbryt
            </Button>
            <Button type="submit" disabled={login.isPending || !token.trim()}>
              {login.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Logga in
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
